import { useEffect, useRef } from 'react';

interface SEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  canonical?: string;
  type?: 'website' | 'profile';
}

/**
 * usePageSEO — dynamically updates <head> meta tags for the current page.
 * Works for real users visiting the React SPA.
 * Search engine / social bots are handled separately by seo-prerender.php via .htaccess.
 */
export function usePageSEO(options: SEOOptions): void {
  const originalTitle = useRef<string>(document.title);

  useEffect(() => {
    const {
      title,
      description,
      keywords,
      ogImage,
      ogUrl,
      canonical,
      type = 'website',
    } = options;

    // Helper: get or create a <meta> tag
    const setMeta = (selector: string, attribute: string, value: string) => {
      if (!value) return;
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        // Set the defining attribute (name or property)
        const [attr, attrVal] = selector.replace(/\[|\]|"/g, '').split('=');
        el.setAttribute(attr, attrVal);
        el.setAttribute('data-seo-injected', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute(attribute, value);
    };

    // Helper: get or create a <link> tag
    const setLink = (rel: string, value: string) => {
      if (!value) return;
      let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('data-seo-injected', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute('href', value);
    };

    // ── Title ──────────────────────────────────────────────────────────────
    if (title) {
      document.title = title;
    }

    // ── Standard meta ──────────────────────────────────────────────────────
    if (description) setMeta('meta[name="description"]', 'content', description);
    if (keywords)    setMeta('meta[name="keywords"]',    'content', keywords);

    // ── Open Graph ─────────────────────────────────────────────────────────
    setMeta('meta[property="og:type"]',        'content', type);
    if (title)       setMeta('meta[property="og:title"]',       'content', title);
    if (description) setMeta('meta[property="og:description"]', 'content', description);
    if (ogImage)     setMeta('meta[property="og:image"]',       'content', ogImage);
    if (ogUrl)       setMeta('meta[property="og:url"]',         'content', ogUrl);
    setMeta('meta[property="og:site_name"]', 'content', 'MY1QR');

    // ── Twitter Card ───────────────────────────────────────────────────────
    setMeta('meta[name="twitter:card"]',        'content', 'summary_large_image');
    if (title)       setMeta('meta[name="twitter:title"]',       'content', title);
    if (description) setMeta('meta[name="twitter:description"]', 'content', description);
    if (ogImage)     setMeta('meta[name="twitter:image"]',       'content', ogImage);

    // ── Canonical ──────────────────────────────────────────────────────────
    if (canonical) setLink('canonical', canonical);

    // ── Cleanup on unmount ─────────────────────────────────────────────────
    return () => {
      // Restore title
      document.title = originalTitle.current;
      // Remove injected tags
      document.head
        .querySelectorAll('[data-seo-injected="true"]')
        .forEach((el) => el.parentNode?.removeChild(el));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options.title,
    options.description,
    options.keywords,
    options.ogImage,
    options.ogUrl,
    options.canonical,
    options.type,
  ]);
}

export default usePageSEO;
